/**
 * useNextAction — DRR-309 Phase 2
 *
 * Determines the priority-sorted next action for a journey.
 * Considers tasks, transitions, deadlines, and regulatory requirements.
 */

import { useMemo } from 'react';
import type { NextAction, AvailableTransition } from '../types';

interface TaskData {
  id: string;
  title: string;
  status: string;
  priority?: string | null;
  deadline?: string | null;
  mandateRef?: string | null;
  taskGroupRef?: string | null;
}

// Priority weights for sorting
const PRIORITY_WEIGHTS: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

// Status that indicates a task is actionable
const ACTIONABLE_STATUSES = ['PENDING', 'IN_PROGRESS', 'ASSIGNED'];

/**
 * Determines the next action based on tasks and available transitions.
 * Priority order:
 * 1. Tasks with regulatory mandate (mandateRef) and deadline < 24h
 * 2. Tasks with critical priority
 * 3. Tasks with high priority or deadline < 48h
 * 4. Available transitions that are ready (guardPassed)
 * 5. Regular pending tasks by priority
 */
export function useNextAction(
  tasks: TaskData[],
  transitions: AvailableTransition[]
): NextAction | null {
  return useMemo(() => {
    const now = new Date();

    // Filter to actionable tasks only
    const actionableTasks = tasks.filter((t) =>
      ACTIONABLE_STATUSES.includes(t.status?.toUpperCase() ?? '')
    );

    // Score each task
    const scoredTasks = actionableTasks.map((task) => {
      let score = 0;
      let priority: NextAction['priority'] = 'medium';
      let deadlineLabel: string | undefined;
      let regulatoryCitation: string | undefined;

      // Base priority score
      const taskPriority = (task.priority ?? 'medium').toLowerCase();
      score += (PRIORITY_WEIGHTS[taskPriority] ?? 2) * 10;

      if (taskPriority === 'critical') priority = 'critical';
      else if (taskPriority === 'high') priority = 'high';
      else if (taskPriority === 'low') priority = 'low';

      // Regulatory mandate bonus
      if (task.mandateRef) {
        score += 50;
        regulatoryCitation = task.mandateRef;
        if (priority !== 'critical') priority = 'high';
      }

      // Deadline urgency
      if (task.deadline) {
        const deadline = new Date(task.deadline);
        const hoursUntilDeadline = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60);

        if (hoursUntilDeadline < 0) {
          score += 100; // Overdue
          deadlineLabel = 'OVERDUE';
          priority = 'critical';
        } else if (hoursUntilDeadline < 24) {
          score += 80;
          deadlineLabel = `${Math.ceil(hoursUntilDeadline)}h remaining`;
          if (priority !== 'critical') priority = 'high';
        } else if (hoursUntilDeadline < 48) {
          score += 40;
          deadlineLabel = `${Math.ceil(hoursUntilDeadline)}h remaining`;
        } else if (hoursUntilDeadline < 72) {
          score += 20;
          const days = Math.ceil(hoursUntilDeadline / 24);
          deadlineLabel = `${days} days remaining`;
        }
      }

      return {
        task,
        score,
        priority,
        deadlineLabel,
        regulatoryCitation,
      };
    });

    // Sort by score descending
    scoredTasks.sort((a, b) => b.score - a.score);

    // Check for ready transitions
    const readyTransition = transitions.find((t) => t.guardPassed);

    // Determine next action
    const top = scoredTasks[0];
    if (top) {
      // If top task is critical or has deadline < 24h, prioritize it
      if (top.priority === 'critical' || top.score >= 80) {
        return {
          type: 'task',
          id: top.task.id,
          title: top.task.title,
          deadline: top.task.deadline ? new Date(top.task.deadline) : null,
          deadlineLabel: top.deadlineLabel,
          regulatoryCitation: top.regulatoryCitation,
          priority: top.priority,
          isBlocked: false,
        };
      }

      // If there's a ready transition and no urgent tasks, suggest transition
      if (readyTransition && top.score < 40) {
        return {
          type: 'transition',
          id: readyTransition.event,
          title: readyTransition.displayName,
          description: `Advance to ${readyTransition.toState.replace(/_/g, ' ')}`,
          priority: 'medium',
          isBlocked: false,
        };
      }

      // Otherwise return top task
      return {
        type: 'task',
        id: top.task.id,
        title: top.task.title,
        deadline: top.task.deadline ? new Date(top.task.deadline) : null,
        deadlineLabel: top.deadlineLabel,
        regulatoryCitation: top.regulatoryCitation,
        priority: top.priority,
        isBlocked: false,
      };
    }

    // No actionable tasks — check transitions
    if (readyTransition) {
      return {
        type: 'transition',
        id: readyTransition.event,
        title: readyTransition.displayName,
        description: `Advance to ${readyTransition.toState.replace(/_/g, ' ')}`,
        priority: 'medium',
        isBlocked: false,
      };
    }

    // Check for blocked transitions
    const blockedTransition = transitions.find((t) => !t.guardPassed);
    if (blockedTransition) {
      return {
        type: 'transition',
        id: blockedTransition.event,
        title: blockedTransition.displayName,
        description: `Complete required tasks to proceed`,
        priority: 'medium',
        isBlocked: true,
        blockedReason: blockedTransition.guard ?? 'Prerequisites not met',
      };
    }

    return null;
  }, [tasks, transitions]);
}

export default useNextAction;
